import { Injectable } from '@angular/core';

/**
 * Configuration constants for deterministic parking calculation
 * These values are FIXED to ensure consistent results
 */
export const PARKING_CONFIG = {
  /** Minimum duration in minutes to qualify as a parking point */
  MIN_DURATION_MINUTES: 1,
  /** Speed threshold - speed must be exactly 0 to be considered parked */
  SPEED_THRESHOLD: 0,
  /** Decimal precision for lat/lng rounding (6 decimals = ~10cm accuracy) */
  COORD_PRECISION: 6,
  /** Decimal precision for duration rounding */
  DURATION_PRECISION: 2
} as const;

/**
 * Interface for GPS playback point data
 */
export interface PlaybackPoint {
  sno?: number;
  Sno?: number;
  lat: number;
  lng: number;
  speed_ct?: number;
  ignition_in?: number;
  add_date?: string;
  Signal?: string;
  Signall?: string;
  asrt_nm?: string;
  stoppageTime?: number;
  lastTime?: string;
  signalTime?: string;
  isParking?: boolean;
  parkingNumber?: number;
  parkingLat?: number;
  parkingLng?: number;
  parkingStartTime?: string;
  parkingEndTime?: string;
  parkingDuration?: number;
  [key: string]: any;
}

/**
 * Interface for parking point data (immutable after creation)
 */
export interface ParkingPoint {
  readonly index: number;
  readonly lat: number;
  readonly lng: number;
  readonly startTime: string;
  readonly endTime: string;
  readonly durationMinutes: number;
  readonly parkingNumber: number;
  readonly startTimestamp: number; // Epoch ms for sorting
  readonly endTimestamp: number;   // Epoch ms for duration verification
}

/**
 * Result of parking calculation (immutable)
 */
export interface ParkingCalculationResult {
  readonly parkingPoints: ReadonlyArray<ParkingPoint>;
  readonly inputHash: string;
  readonly calculatedAt: number;
  readonly totalCount: number;
  readonly totalDurationMinutes: number;
}

/**
 * PlaybackService - Deterministic parking points calculation
 *
 * GUARANTEES:
 * 1. Same input data ALWAYS produces same output
 * 2. Data is sorted by timestamp before processing
 * 3. Lat/lng are rounded to fixed precision
 * 4. Results are immutable (frozen objects)
 * 5. No external state dependencies
 */
@Injectable({
  providedIn: 'root'
})
export class PlaybackService {

  constructor() { }

  /**
   * Round a number to fixed decimal places (deterministic)
   */
  private roundToFixed(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  /**
   * Generate a hash of input data for consistency verification
   * This helps detect if the same data produces different results
   */
  private generateInputHash(data: PlaybackPoint[]): string {
    if (!data || data.length === 0) return 'empty';

    const first = data[0];
    const last = data[data.length - 1];
    const firstTs = first.add_date || first.Signal || first.Signall || '';
    const lastTs = last.add_date || last.Signal || last.Signall || '';

    return `${data.length}_${firstTs}_${lastTs}_${first.lat?.toFixed(4)}_${last.lat?.toFixed(4)}`;
  }

  /**
   * Parse timestamp string to milliseconds (epoch time)
   * Returns null if parsing fails - deterministic parsing
   *
   * Supports formats:
   * - "DD-MM-YYYY HH:mm:ss"
   * - "DD-MM-YYYY HH:mm"
   */
  parseTimestamp(timestampStr: string): Date | null {
    if (!timestampStr || typeof timestampStr !== 'string') return null;

    const ts = timestampStr.trim();
    if (ts.length === 0) return null;

    const parts = ts.split(' ');
    if (parts.length < 2) return null;

    const datePart = parts[0].split('-');
    const timePart = parts[1].split(':');

    if (datePart.length !== 3 || timePart.length < 2) return null;

    const day = parseInt(datePart[0], 10);
    const month = parseInt(datePart[1], 10) - 1; // 0-indexed
    const year = parseInt(datePart[2], 10);
    const hour = parseInt(timePart[0], 10);
    const minute = parseInt(timePart[1], 10);
    const second = timePart.length >= 3 ? parseInt(timePart[2], 10) : 0;

    // Validate all parsed values
    if (isNaN(day) || isNaN(month) || isNaN(year) ||
        isNaN(hour) || isNaN(minute) || isNaN(second)) {
      return null;
    }

    // Validate ranges
    if (day < 1 || day > 31 || month < 0 || month > 11 ||
        hour < 0 || hour > 23 || minute < 0 || minute > 59 ||
        second < 0 || second > 59) {
      return null;
    }

    const date = new Date(year, month, day, hour, minute, second);

    // Verify the date is valid (handles edge cases like Feb 30)
    if (isNaN(date.getTime())) return null;

    return date;
  }

  /**
   * Get timestamp in milliseconds for sorting
   */
  private getTimestampMs(point: PlaybackPoint): number {
    const ts = point.add_date || point.Signal || point.Signall;
    if (!ts) return 0;

    const date = this.parseTimestamp(ts);
    return date ? date.getTime() : 0;
  }

  /**
   * Sort data by timestamp (CRITICAL for deterministic results)
   * Returns a NEW sorted array - does not mutate input
   */
  private sortByTimestamp(data: PlaybackPoint[]): PlaybackPoint[] {
    // Create a shallow copy with timestamp cache for stable sorting
    const withTimestamp = data.map((point, originalIndex) => ({
      point,
      timestamp: this.getTimestampMs(point),
      originalIndex
    }));

    // Sort by timestamp, then by original index for stability
    withTimestamp.sort((a, b) => {
      if (a.timestamp !== b.timestamp) {
        return a.timestamp - b.timestamp;
      }
      return a.originalIndex - b.originalIndex;
    });

    return withTimestamp.map(item => item.point);
  }

  /**
   * Check if a point qualifies as "parked"
   * NEW LOGIC: Only speed = 0 is considered parked (ignition check removed)
   */
  private isPointParked(point: PlaybackPoint, useIgnition: boolean): boolean {
    const speed = point.speed_ct ?? 0;

    // Speed must be exactly 0 to be considered parked
    return speed === PARKING_CONFIG.SPEED_THRESHOLD;
  }

  /**
   * Check if two coordinates are the same (within precision)
   * Used to merge consecutive points with same lat/lng
   */
  private areCoordinatesSame(lat1: number, lng1: number, lat2: number, lng2: number): boolean {
    const precision = Math.pow(10, -PARKING_CONFIG.COORD_PRECISION);
    return Math.abs(lat1 - lat2) < precision && Math.abs(lng1 - lng2) < precision;
  }

  /**
   * Determine if ignition data should be used
   * Uses deterministic check - at least 50% of points must have ignition data
   */
  private shouldUseIgnitionData(data: PlaybackPoint[]): boolean {
    if (data.length === 0) return false;

    let countWithIgnition = 0;
    for (const point of data) {
      if (point.ignition_in !== undefined && point.ignition_in !== null) {
        countWithIgnition++;
      }
    }

    // Use >= for deterministic boundary (exactly 50% counts as having data)
    const threshold = Math.floor(data.length / 2);
    return countWithIgnition >= threshold;
  }

  /**
   * MAIN METHOD: Calculate parking points deterministically
   *
   * NEW ALGORITHM:
   * 1. Sort data by timestamp (ensures consistent sequential order)
   * 2. Find consecutive points where speed = 0 (only speed check, no ignition)
   * 3. Group consecutive parked points (speed = 0) into single parking events
   * 4. If consecutive lat/lng are the same, they are automatically merged (same block)
   * 5. Calculate duration from first point (point A) to last point (point B) in the group
   * 6. Use constant lat/lng (first point's coordinates) as parking location
   * 7. Filter by minimum duration threshold
   * 8. Round coordinates and durations to fixed precision
   * 9. Return immutable frozen results
   *
   * EXAMPLE:
   * Points with speed = 0 from 06:09:07 to 06:24:26:
   * - All points are grouped as ONE parking point
   * - Duration = time difference (06:24:26 - 06:09:07)
   * - Lat/lng = first point's coordinates (constant)
   * - If lat/lng are same for consecutive points, they're merged automatically
   *
   * @param data - Array of GPS points (will not be mutated)
   * @returns Immutable ParkingCalculationResult
   */
  calculateParkingPointsDeterministic(data: PlaybackPoint[]): ParkingCalculationResult {
    const calculatedAt = Date.now();
    const inputHash = this.generateInputHash(data);

    // Handle empty/null input
    if (!data || !Array.isArray(data) || data.length === 0) {
      return Object.freeze({
        parkingPoints: Object.freeze([]),
        inputHash: 'empty',
        calculatedAt,
        totalCount: 0,
        totalDurationMinutes: 0
      });
    }

    console.log(`[ParkingCalc] Starting deterministic calculation for ${data.length} points, hash: ${inputHash}`);

    // Step 1: Sort data by timestamp (CRITICAL - ensures sequential order)
    const sortedData = this.sortByTimestamp(data);

    // Step 2: Find parking blocks - only consider speed = 0 (ignition check removed)
    const parkingPoints: ParkingPoint[] = [];
    let parkingBlockStart: {
      index: number;
      startTime: Date;
      startTimeStr: string;
      startTimestamp: number;
      lat: number;
      lng: number;
    } | null = null;

    let parkingNumber = 1;

    for (let i = 0; i < sortedData.length; i++) {
      const point = sortedData[i];
      const timestamp = point.add_date || point.Signal || point.Signall;

      if (!timestamp) continue;

      const pointTime = this.parseTimestamp(timestamp);
      if (!pointTime) continue;

      const pointTimestamp = pointTime.getTime();
      const speed = point.speed_ct ?? 0;
      const isParked = speed === PARKING_CONFIG.SPEED_THRESHOLD; // Only check speed = 0

      if (isParked) {
        // Start or continue a parking block
        if (parkingBlockStart === null) {
          // Start new parking block
          parkingBlockStart = {
            index: i,
            startTime: pointTime,
            startTimeStr: timestamp,
            startTimestamp: pointTimestamp,
            lat: this.roundToFixed(point.lat, PARKING_CONFIG.COORD_PRECISION),
            lng: this.roundToFixed(point.lng, PARKING_CONFIG.COORD_PRECISION)
          };
        } else {
          // Continue existing parking block
          // Check if current point has same lat/lng as previous point
          const prevPoint = sortedData[i - 1];
          const currentLat = this.roundToFixed(point.lat, PARKING_CONFIG.COORD_PRECISION);
          const currentLng = this.roundToFixed(point.lng, PARKING_CONFIG.COORD_PRECISION);
          const prevLat = this.roundToFixed(prevPoint.lat, PARKING_CONFIG.COORD_PRECISION);
          const prevLng = this.roundToFixed(prevPoint.lng, PARKING_CONFIG.COORD_PRECISION);

          // If coordinates are the same, merge into same parking point (continue block)
          // If coordinates differ, still continue the block (same parking event, vehicle moved slightly)
          // The block will be closed when speed becomes non-zero
        }
      } else {
        // Speed is not 0 - close any open parking block
        if (parkingBlockStart !== null) {
          // Get the previous point (last point that was parked - point B)
          const lastParkedIndex = i - 1;
          if (lastParkedIndex >= 0 && lastParkedIndex < sortedData.length) {
            const lastParkedPoint = sortedData[lastParkedIndex];
            const endTimestamp = lastParkedPoint.add_date || lastParkedPoint.Signal || lastParkedPoint.Signall;
            const endTime = this.parseTimestamp(endTimestamp || '');

            if (endTime) {
              // Calculate duration from point A (start) to point B (end)
              const durationMs = endTime.getTime() - parkingBlockStart.startTimestamp;
              const durationMinutes = this.roundToFixed(
                durationMs / (1000 * 60),
                PARKING_CONFIG.DURATION_PRECISION
              );

              // Only add if duration meets minimum threshold
              if (durationMinutes >= PARKING_CONFIG.MIN_DURATION_MINUTES) {
                // Use constant lat/lng from first point (point A) as parking location
                parkingPoints.push(Object.freeze({
                  index: parkingBlockStart.index,
                  lat: parkingBlockStart.lat, // Constant lat from first point
                  lng: parkingBlockStart.lng, // Constant lng from first point
                  startTime: parkingBlockStart.startTimeStr, // Point A timestamp
                  endTime: endTimestamp || '', // Point B timestamp
                  durationMinutes, // Duration from A to B
                  parkingNumber: parkingNumber++,
                  startTimestamp: parkingBlockStart.startTimestamp,
                  endTimestamp: endTime.getTime()
                }));
              }
            }
          }

          // Reset block tracking
          parkingBlockStart = null;
        }
      }
    }

    // Handle case where parking continues until end of data
    if (parkingBlockStart !== null) {
      const lastPoint = sortedData[sortedData.length - 1];
      const endTimestamp = lastPoint.add_date || lastPoint.Signal || lastPoint.Signall;
      const endTime = this.parseTimestamp(endTimestamp || '');

      if (endTime) {
        // Calculate duration from point A (start) to point B (end - last point)
        const durationMs = endTime.getTime() - parkingBlockStart.startTimestamp;
        const durationMinutes = this.roundToFixed(
          durationMs / (1000 * 60),
          PARKING_CONFIG.DURATION_PRECISION
        );

        if (durationMinutes >= PARKING_CONFIG.MIN_DURATION_MINUTES) {
          // Use constant lat/lng from first point (point A) as parking location
          parkingPoints.push(Object.freeze({
            index: parkingBlockStart.index,
            lat: parkingBlockStart.lat, // Constant lat from first point
            lng: parkingBlockStart.lng, // Constant lng from first point
            startTime: parkingBlockStart.startTimeStr, // Point A timestamp
            endTime: endTimestamp || '', // Point B timestamp (last point)
            durationMinutes, // Duration from A to B
            parkingNumber: parkingNumber++,
            startTimestamp: parkingBlockStart.startTimestamp,
            endTimestamp: endTime.getTime()
          }));
        }
      }
    }

    // Calculate totals
    const totalDurationMinutes = this.roundToFixed(
      parkingPoints.reduce((sum, p) => sum + p.durationMinutes, 0),
      PARKING_CONFIG.DURATION_PRECISION
    );

    console.log(`[ParkingCalc] Found ${parkingPoints.length} parking points, total duration: ${totalDurationMinutes} min`);

    // Return immutable result
    return Object.freeze({
      parkingPoints: Object.freeze(parkingPoints),
      inputHash,
      calculatedAt,
      totalCount: parkingPoints.length,
      totalDurationMinutes
    });
  }

  /**
   * Legacy method - delegates to deterministic calculation
   * @deprecated Use calculateParkingPointsDeterministic instead
   */
  getParkingPoints(data: PlaybackPoint[]): ParkingPoint[] {
    const result = this.calculateParkingPointsDeterministic(data);
    // Return a mutable copy for backward compatibility
    return result.parkingPoints.map(p => ({ ...p }));
  }

  /**
   * Apply parking data to playback points (immutable operation)
   * Returns a NEW array - does not mutate input
   */
  applyParkingDataToPoints(playbackPoints: PlaybackPoint[], parkingPoints: ReadonlyArray<ParkingPoint>): PlaybackPoint[] {
    // Create parking lookup by start time for O(1) access
    const parkingByStartTime = new Map<string, ParkingPoint>();
    for (const parking of parkingPoints) {
      parkingByStartTime.set(parking.startTime, parking);
    }

    // Create new array with parking data applied
    return playbackPoints.map((point) => {
      const timestamp = point.add_date || point.Signal || point.Signall;
      const parking = timestamp ? parkingByStartTime.get(timestamp) : undefined;

      if (parking) {
        return {
          ...point,
          isParking: true,
          Sno: parking.parkingNumber,
          stoppageTime: parking.durationMinutes,
          lastTime: parking.endTime,
          signalTime: parking.startTime,
          parkingNumber: parking.parkingNumber,
          parkingLat: parking.lat,
          parkingLng: parking.lng,
          parkingStartTime: parking.startTime,
          parkingEndTime: parking.endTime,
          parkingDuration: parking.durationMinutes
        };
      }
      return { ...point }; // Return copy even if no parking data
    });
  }

  /**
   * Format parking duration for display
   */
  formatParkingDuration(durationMinutes: number): string {
    if (durationMinutes >= 60) {
      const hours = Math.floor(durationMinutes / 60);
      const minutes = Math.round(durationMinutes % 60);
      return `${hours}h ${minutes}m`;
    }
    return `${Math.round(durationMinutes)}m`;
  }

  /**
   * Calculate parking duration between two timestamps
   */
  calculateParkingDuration(startTime: string, endTime: string): number {
    const start = this.parseTimestamp(startTime);
    const end = this.parseTimestamp(endTime);

    if (!start || !end) return 0;

    const durationMs = end.getTime() - start.getTime();
    return Math.max(0, this.roundToFixed(durationMs / (1000 * 60), PARKING_CONFIG.DURATION_PRECISION));
  }

  /**
   * Get summary of parking points
   */
  getParkingSummary(parkingPoints: ReadonlyArray<ParkingPoint>): {
    count: number;
    totalDuration: number;
    formattedDuration: string
  } {
    const count = parkingPoints.length;
    const totalDuration = this.roundToFixed(
      parkingPoints.reduce((sum, p) => sum + p.durationMinutes, 0),
      PARKING_CONFIG.DURATION_PRECISION
    );

    return {
      count,
      totalDuration,
      formattedDuration: this.formatParkingDuration(totalDuration)
    };
  }

  /**
   * Verify calculation consistency - call with same data twice to verify
   */
  verifyConsistency(data: PlaybackPoint[]): boolean {
    const result1 = this.calculateParkingPointsDeterministic(data);
    const result2 = this.calculateParkingPointsDeterministic(data);

    if (result1.totalCount !== result2.totalCount) {
      console.error('[ParkingCalc] CONSISTENCY ERROR: Different count', result1.totalCount, result2.totalCount);
      return false;
    }

    if (result1.inputHash !== result2.inputHash) {
      console.error('[ParkingCalc] CONSISTENCY ERROR: Different hash', result1.inputHash, result2.inputHash);
      return false;
    }

    for (let i = 0; i < result1.parkingPoints.length; i++) {
      const p1 = result1.parkingPoints[i];
      const p2 = result2.parkingPoints[i];

      if (p1.lat !== p2.lat || p1.lng !== p2.lng || p1.durationMinutes !== p2.durationMinutes) {
        console.error('[ParkingCalc] CONSISTENCY ERROR: Different point data at index', i);
        return false;
      }
    }

    console.log('[ParkingCalc] Consistency verified: PASS');
    return true;
  }
}
